import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setLoading } from 'Actions/loading';
import { Grid, Box, Typography, Button, Select, FormControl, MenuItem, InputLabel } from '@mui/material';
import { PaginationBar } from 'Shared/PaginationBar';
import { AdminFlowsItem } from 'Components/shared/flows/AdminFlowsItem';
import { AdminFlowsItemV2 } from 'Components/shared/flows/AdminFlowsItemV2';
import FlowCreateForm from 'Shared/flows/flows-interface/FlowCreateForm';
import FlowExportFlow from 'Shared/flows/flows-interface/FlowExportFlow';
import FlowBulkExport from 'Shared/flows/flows-interface/FlowBulkExport';
import FlowBulkExportBar from 'Shared/flows/flows-interface/FlowBulkExportBar';
import FlowSelectAll from 'Shared/flows/flows-interface/FlowSelectAll';
import AiPromptDialog from 'Shared/flows/flows-interface/AiPromptDialog';
import { CompanyAccessModal } from 'Components/shared/modals/CompanyAccessModal';
import { AddIcon, WhiteAddIcon } from 'Shared/Icons';
import FoldersSidebar from 'Shared/flows/folders/FoldersSidebar';
import CreateFolderModal from 'Shared/flows/folders/CreateFolderModal';
import { useFolders } from 'Shared/flows/folders/useFolders';
import { getFlowFolder, getFlowFolderPath } from 'Shared/flows/folders/folderUtils';
import { useFoundCounts } from 'Shared/flows/folders/useFoundCounts';
import FlowSearch from 'Shared/flows/folders/FlowSearch';
import http from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import config from '../config';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { useSnackbar } from 'notistack';
import { useCreateUrl } from 'Hooks/useCreateUrl';
import { useFlowSelection } from 'Hooks/useFlowSelection';
import { UserPreDefinedFlowsItem } from 'Components/shared/flows/UserPreDefinedFlowsItem';

const AdminFlowsPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { createUrl } = useCreateUrl();
    const currentUser = useSelector(state => state.auth?.user);
    const userRole = currentUser?.access;

    const [flowsList, setFlowsList] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [userFlowList, setUserFlowList] = useState([]);
    const [user, setUser] = useState('');
    const [visibleCreateFlowForm, setVisibleCreateFlowForm] = useState(false);
    const [needUpdateList, setNeedUpdateList] = useState(false);
    const [myCompanyFlows, setMyCompanyFlows] = useState([]);
    const [companyAccessModalOpen, setCompanyAccessModalOpen] = useState(false);
    const [selectedFlowForAccess, setSelectedFlowForAccess] = useState(null);

    const [isImportFlow, setIsImportFlow] = useState(false);
    const [isAiPromptDialog, setIsAiPromptDialog] = useState(false);
    const [isBulkExport, setIsBulkExport] = useState(false);
    const { selectedFlows, isSelected, toggleFlow, removeFlow, toggleVisible, clearSelection } = useFlowSelection();
    // Pagination / applied search live in the URL, so they survive navigating
    // into a flow and back (and page refresh). URL is the source of truth.
    const [searchParams, setSearchParams] = useSearchParams();

    const flowsPage = parseInt(searchParams.get('flowsPage') || '0', 10);
    const flowsRowsPerPage = parseInt(searchParams.get('flowsRows') || '10', 10);
    const companyFlowsPage = parseInt(searchParams.get('companyPage') || '0', 10);
    const companyFlowsRowsPerPage = parseInt(searchParams.get('companyRows') || '10', 10);
    const userFlowsPage = parseInt(searchParams.get('userPage') || '0', 10);
    const userFlowsRowsPerPage = parseInt(searchParams.get('userRows') || '10', 10);
    const appliedSearch = searchParams.get('search') || '';

    const [searchText, setSearchText] = useState(appliedSearch);
    const [flowsTotal, setFlowsTotal] = useState(0);
    const [companyFlowsTotal, setCompanyFlowsTotal] = useState(0);
    const [userFlowsTotal, setUserFlowsTotal] = useState(0);

    const updateParams = useCallback((updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, value]) => {
                if (value === '' || value === null || value === undefined) {
                    next.delete(key);
                } else {
                    next.set(key, String(value));
                }
            });
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const {
        folders, fetchFolders, view, folderId, selectedFolder, isSearchAll, listFolder,
        modal, openCreateFolder, openRenameFolder, closeFolderModal,
    } = useFolders();

    const foundCounts = useFoundCounts(userRole === 'Admin' ? '/flows' : '/user/flow/list', isSearchAll ? appliedSearch : '', folders);

    // The view and the folder go into the URL together with the reset paging:
    // a single update, otherwise the last one would overwrite the others.
    const selectView = useCallback((v) => {
        updateParams({
            view: v,
            folder: null,
            flowsPage: 0, companyPage: 0, userPage: 0,
        });
    }, [updateParams]);

    const selectFolder = useCallback((id) => {
        updateParams({ view: 'folders', folder: id, flowsPage: 0, companyPage: 0, userPage: 0 });
    }, [updateParams]);

    // Keep the search input in sync with the URL (so the FLOWS menu link, which
    // drops the query, also clears the input; browser Back restores it).
    useEffect(() => setSearchText(appliedSearch), [appliedSearch]);

    const toCreateFlow = (e) => {
        e.preventDefault();
        setVisibleCreateFlowForm(true);
    };

    const getPreDefinedFlowsList = useCallback(async () => {
        try {
            dispatch(setLoading(true));
            const pagination = JSON.stringify({ pageSize: flowsRowsPerPage, currentPage: flowsPage });
            const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
            const folderParam = userRole === 'Admin' ? `&folder_id=${encodeURIComponent(listFolder)}` : '';
            const res = await cachedGet(`/flows?pagination=${encodeURIComponent(pagination)}${searchParam}${folderParam}`);
            
            if (!res.data) {
                setFlowsList([]);
                dispatch(setLoading(false));
                return;
            }
            
            if (!res.data.flows) {
                setFlowsList([]);
                dispatch(setLoading(false));
                return;
            }
            
            // Check if flows is an object or array
            let flowsArray;
            if (Array.isArray(res.data.flows)) {
                flowsArray = res.data.flows;
            } else if (typeof res.data.flows === 'object' && res.data.flows !== null) {
                // Convert object to array
                flowsArray = Object.values(res.data.flows);
            } else {
                setFlowsList([]);
                dispatch(setLoading(false));
                return;
            }
            
            // Backend now handles access via company_flow_access table, no need to filter by access field
            setFlowsList(flowsArray);
            setFlowsTotal(res.data.total ?? flowsArray.length);
            dispatch(setLoading(false));
        } catch (err) {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response?.data?.message || 'Error loading flows'}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        }
    }, [dispatch, enqueueSnackbar, flowsPage, flowsRowsPerPage, appliedSearch, listFolder, userRole]);

    const setUserAndGetFlows = (user_id) => {
        setUser(user_id);
        updateParams({ userPage: 0 });
        getFlowUser(user_id);
    };

    const getFlowUser = (user_id) => {
        if (!user_id) {
            enqueueSnackbar(`Please select user`, {
                action: CustomSnackbar,
                variant: 'warning'
            });
            return;
        }

        dispatch(setLoading(true));
        const pagination = JSON.stringify({ pageSize: userFlowsRowsPerPage, currentPage: userFlowsPage });
        const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
        cachedGet(`/user/flow/list?user_id=${user_id}&pagination=${encodeURIComponent(pagination)}${searchParam}`)
            .then((res) => {
                if (!res.data) {
                    setUserFlowList([]);
                    dispatch(setLoading(false));
                    return;
                }
                
                if (!res.data.flows) {
                    setUserFlowList([]);
                    dispatch(setLoading(false));
                    return;
                }
                
                // Check if flows is an object or array
                let flowsArray;
                if (Array.isArray(res.data.flows)) {
                    flowsArray = res.data.flows;
                } else if (typeof res.data.flows === 'object' && res.data.flows !== null) {
                    // Convert object to array
                    flowsArray = Object.values(res.data.flows);
                } else {
                    setUserFlowList([]);
                    dispatch(setLoading(false));
                    return;
                }
                
                const mapped = flowsArray.map((item) => {
                    return {
                        id: item.flow_id,
                        user_id: user_id,
                        current_user_id: user_id, // Add current_user_id for compatibility
                        ...item,
                    };
                });

                setUserFlowList(mapped);
                setUserFlowsTotal(res.data.total ?? mapped.length);
                dispatch(setLoading(false));
            }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response?.data?.message || 'Error loading user flows'}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    const deleteFlow = async (item) => {
        try {
            dispatch(setLoading(true));
            await http.delete(`/flow/${item.id}/delete`);

            await getPreDefinedFlowsList();
            getMyCompanyFlowsList();

            if (user) {
                await getFlowUser(user);
            }

            dispatch(setLoading(false));
            enqueueSnackbar('Flow was deleted', {
                action: CustomSnackbar,
                variant: 'success'
            });
        } catch (err) {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        }
    };

    const deleteFlowUser = (item) => {
        const user_id = item.user_id;
        const url = createUrl(`/flow/${item.id}/delete`);
        dispatch(setLoading(true));
        http.delete(url).then(() => {
            getPreDefinedFlowsList();
            getFlowUser(user_id);
            dispatch(setLoading(false));
            enqueueSnackbar('Flow was deleted', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    const changeReadonlyFlow = (item) => {
        const currentReadonly = item.readonly === 1 || item.readonly === '1' || item.readonly === true;
        const nextReadonly = currentReadonly ? 0 : 1;
        const params = {
            flow_id: item.id,
            readonly: nextReadonly
        };
        dispatch(setLoading(true));
        http.post(`/flow/${item.id}/update`, params).then((res) => {
            setMyCompanyFlows(prev =>
                prev.map(f => f.id === item.id ? { ...f, ...res.data.flow } : f)
            );
            // Also update userFlowList for visual switch update
            setUserFlowList(prev =>
                prev.map(f => f.id === item.id ? { ...f, ...res.data.flow } : f)
            );
            // Also update flowsList for Available Pre-Defined Flows section
            setFlowsList(prev =>
                prev.map(f => f.id === item.id ? { ...f, ...res.data.flow } : f)
            );
            dispatch(setLoading(false));
            enqueueSnackbar('Flow readonly status was changed', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response?.data?.message || 'Error updating flow readonly status'}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    useEffect(() => {
        cachedGet('/users').then((res) => {
            if (!res.data) {
                setUsersList([]);
                return;
            }
            
            if (!res.data.users) {
                setUsersList([]);
                return;
            }
            
            // Exclude current user from the list
            const filteredUsers = res.data.users.filter(user => user.id !== currentUser?.id);
            setUsersList(filteredUsers);
        }).catch((err) => {
            setUsersList([]);
        });
    }, [currentUser?.id]);

    useEffect(() => {
        if (needUpdateList) {
            getPreDefinedFlowsList().then(() => setNeedUpdateList(false));
        }
    }, [needUpdateList, getPreDefinedFlowsList, setNeedUpdateList]);

    useEffect(() => {
        getPreDefinedFlowsList();
    }, [flowsPage, flowsRowsPerPage, appliedSearch, listFolder]);

    useEffect(() => {
        if (user) {
            getFlowUser(user);
        }
    }, [user, userFlowsPage, userFlowsRowsPerPage, appliedSearch]);

    const openFoundFlow = (flow) => {
        const folder = getFlowFolder(folders, flow.id);
        if (flow.user_id !== currentUser?.id && usersList.some((item) => item.id === flow.user_id)) {
            setUser(flow.user_id);
        }
        updateParams({
            view: folder ? 'folders' : 'all',
            folder: folder ? folder.id : null,
            flowsPage: 0,
            companyPage: 0,
            userPage: 0,
            search: flow.name,
        });
    };

    const findFlows = (text) => {
        updateParams({ view: 'all', folder: null, flowsPage: 0, companyPage: 0, userPage: 0, search: text });
    };

    const handleClear = () => {
        setSearchText('');
        updateParams({ flowsPage: 0, companyPage: 0, userPage: 0, search: '' });
    };

    useEffect(() => {
        const pagination = JSON.stringify({ pageSize: companyFlowsRowsPerPage, currentPage: companyFlowsPage });
        const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
        cachedGet(`/user/flow/list?pagination=${encodeURIComponent(pagination)}${searchParam}&folder_id=${encodeURIComponent(listFolder)}`)
            .then((res) => {
                if (!res.data) {
                    setMyCompanyFlows([]);
                    return;
                }

                if (!res.data.flows) {
                    setMyCompanyFlows([]);
                    return;
                }

                // Check if flows is an object or array
                let flowsArray;
                if (Array.isArray(res.data.flows)) {
                    flowsArray = res.data.flows;
                } else if (typeof res.data.flows === 'object' && res.data.flows !== null) {
                    // Convert object to array
                    flowsArray = Object.values(res.data.flows);
                } else {
                    setMyCompanyFlows([]);
                    return;
                }

                setMyCompanyFlows(flowsArray || []);
                setCompanyFlowsTotal(res.data.total ?? (flowsArray || []).length);
            })
            .catch((err) => {
                setMyCompanyFlows([]);
            });
    }, [companyFlowsPage, companyFlowsRowsPerPage, appliedSearch, listFolder]);

    // Function for updating company flows list
    const getMyCompanyFlowsList = useCallback(() => {
        const pagination = JSON.stringify({ pageSize: companyFlowsRowsPerPage, currentPage: companyFlowsPage });
        const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
        cachedGet(`/user/flow/list?pagination=${encodeURIComponent(pagination)}${searchParam}&folder_id=${encodeURIComponent(listFolder)}`)
            .then((res) => {
                if (!res.data) {
                    setMyCompanyFlows([]);
                    return;
                }

                if (!res.data.flows) {
                    setMyCompanyFlows([]);
                    return;
                }

                // Check if flows is an object or array
                let flowsArray;
                if (Array.isArray(res.data.flows)) {
                    flowsArray = res.data.flows;
                } else if (typeof res.data.flows === 'object' && res.data.flows !== null) {
                    // Convert object to array
                    flowsArray = Object.values(res.data.flows);
                } else {
                    setMyCompanyFlows([]);
                    return;
                }

                setMyCompanyFlows(flowsArray || []);
                setCompanyFlowsTotal(res.data.total ?? (flowsArray || []).length);
            })
            .catch((err) => {
                setMyCompanyFlows([]);
            });
    }, [companyFlowsPage, companyFlowsRowsPerPage, appliedSearch, listFolder]);

    // Add function for copying public flow to self (User/Company Admin)
    const linkFlowToUser = async (item) => {
        try {
            dispatch(setLoading(true));
            await http.post(`/user/flow/${item.id}/copy`);
            enqueueSnackbar('Flow was added to your active flows', {
                action: CustomSnackbar,
                variant: 'success'
            });
            setNeedUpdateList(true);
            getMyCompanyFlowsList();
        } catch (err) {
            enqueueSnackbar(`${err?.response?.data?.message || 'Error copying flow'}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        } finally {
            dispatch(setLoading(false));
        }
    };

    // Company Access Modal handlers
    const handleOpenCompanyAccessModal = (flowId) => {
        setSelectedFlowForAccess(flowId);
        setCompanyAccessModalOpen(true);
    };

    const handleCloseCompanyAccessModal = () => {
        setCompanyAccessModalOpen(false);
        setSelectedFlowForAccess(null);
    };

    const handleSaveCompanyAccess = (hasActiveCompany) => {
        setFlowsList((prevFlows) =>
            prevFlows.map((flow) =>
                flow.id === selectedFlowForAccess
                    ? { ...flow, is_active_for_any_company: hasActiveCompany }
                    : flow
            )
        );
        setUserFlowList((prevFlows) =>
            prevFlows.map((flow) =>
                flow.id === selectedFlowForAccess
                    ? { ...flow, is_active_for_any_company: hasActiveCompany }
                    : flow
            )
        );
        setMyCompanyFlows((prevFlows) =>
            prevFlows.map((flow) =>
                flow.id === selectedFlowForAccess
                    ? { ...flow, is_active_for_any_company: hasActiveCompany }
                    : flow
            )
        );
    };

    return (
        <>
            <FlowExportFlow visible={isImportFlow} setVisible={setIsImportFlow} update={setNeedUpdateList} folderId={selectedFolder === 'all' ? null : selectedFolder} onFoldersChanged={fetchFolders}/>
            <FlowBulkExport
                visible={isBulkExport}
                setVisible={setIsBulkExport}
                flows={selectedFlows}
                onRemove={removeFlow}
                onExported={clearSelection}
            />
            <AiPromptDialog visible={isAiPromptDialog} setVisible={setIsAiPromptDialog}/>
            {visibleCreateFlowForm && (
                <FlowCreateForm
                    visibleCreateFlowForm={visibleCreateFlowForm}
                    setVisibleCreateFlowForm={setVisibleCreateFlowForm}
                />
            )}
            <Grid container sx={{height: '100%'}}>
                <Grid item
                      xs={12}
                      md={4}
                      lg={3}
                      sx={{
                          paddingBottom: '20px',
                          '@media (min-width: 1024px)': {
                              paddingRight: '20px',
                              paddingBottom: '0',
                          },
                      }}>
                    <Box
                        sx={{
                            height: 'calc(100vh - 30px)',
                            backgroundColor: '#f9f7f5',
                            border: '1px solid #FFFFFF',
                            boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            padding: '20px',
                            position: 'sticky',
                            top: '15px'
                        }}
                    >
                        <FoldersSidebar
                            folders={folders}
                            view={view}
                            selectedFolderId={folderId}
                            onSelectView={selectView}
                            onSelectFolder={selectFolder}
                            onFoldersChanged={fetchFolders}
                            onFlowChanged={() => { getPreDefinedFlowsList(); getMyCompanyFlowsList(); }}
                            onCreateChild={(folder) => openCreateFolder(folder.id)}
                            onRename={openRenameFolder}
                            foundCounts={foundCounts}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={8} lg={9}>
                    <Box
                        sx={{
                            height: '100%',
                            padding: '20px',
                            backgroundColor: '#FAFEFF',
                            border: '1px solid #FFFFFF',
                            boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px', justifyContent: 'flex-end' }}>
                            <FlowSearch
                                value={searchText}
                                onChange={setSearchText}
                                onSelect={openFoundFlow}
                                onFind={findFlows}
                                folders={folders}
                                sources={['/flows', '/user/flow/list', ...usersList.map((item) => `/user/flow/list?user_id=${item.id}`)]}
                            />
                            <Button variant="outlined" onClick={handleClear}>Clear</Button>
                        </Box>

                        {userRole === 'Admin' && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '32px',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Typography
                                    variant="h2"
                                    color="primary.blackText"
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: '20px',
                                        lineHeight: '40px',
                                    }}
                                >
                                    Available Pre-Defined Flows:
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    gap: '10px',
                                    flexWrap: 'wrap'
                                }}>
                                    <Button
                                        onClick={() => openCreateFolder()}
                                        startIcon={<WhiteAddIcon/>}
                                        variant="contained"
                                    >
                                        Create Folder
                                    </Button>
                                    <Button
                                        onClick={() => setIsImportFlow(true)}
                                        variant="contained"
                                    >
                                        Import Flow
                                    </Button>
                                    <FlowBulkExportBar
                                        selectedFlows={selectedFlows}
                                        clearSelection={clearSelection}
                                        onExport={() => setIsBulkExport(true)}
                                    />
                                    {config.AI_ENABLED && (
                                        <Button
                                            onClick={() => setIsAiPromptDialog(true)}
                                            variant="contained"
                                            color="secondary"
                                        >
                                            AI Flow Builder
                                        </Button>
                                    )}
                                    <Button
                                        onClick={toCreateFlow}
                                        startIcon={<AddIcon/>}
                                        variant="outlined"
                                    >
                                        Create New Flow
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {userRole === 'Company Admin' && (
                            <>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '32px',
                                    flexWrap: 'wrap',
                                    gap: '10px'
                                }}>
                                    <Typography
                                        variant="h2"
                                        color="primary.blackText"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '20px',
                                            lineHeight: '40px',
                                        }}
                                    >
                                        Company Pre-Defined Flows:
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Button
                                            onClick={() => openCreateFolder()}
                                            startIcon={<WhiteAddIcon/>}
                                            variant="contained"
                                        >
                                            Create Folder
                                        </Button>
                                        <Button
                                            onClick={() => setIsImportFlow(true)}
                                            variant="contained"
                                        >
                                            Import Flow
                                        </Button>
                                        <FlowBulkExportBar
                                            selectedFlows={selectedFlows}
                                            clearSelection={clearSelection}
                                            onExport={() => setIsBulkExport(true)}
                                        />
                                        {config.AI_ENABLED && (
                                            <Button
                                                onClick={() => setIsAiPromptDialog(true)}
                                                variant="contained"
                                                color="secondary"
                                            >
                                                AI Flow Builder
                                            </Button>
                                        )}
                                        <Button
                                            onClick={toCreateFlow}
                                            startIcon={<AddIcon/>}
                                            variant="outlined"
                                        >
                                            Create New Flow
                                        </Button>
                                    </Box>
                                </Box>
                                {!myCompanyFlows.length && (
                                    <Typography
                                        sx={{
                                            marginBottom: '20px',
                                            fontWeight: 500,
                                            fontSize: '20px',
                                            lineHeight: '24px',
                                            color: '#D76A6A',
                                            textAlign: 'center',
                                        }}
                                    >
                                        No company flows found
                                    </Typography>
                                )}
                                <FlowSelectAll
                                    items={myCompanyFlows}
                                    selectedFlows={selectedFlows}
                                    toggleVisible={toggleVisible}
                                />
                                {myCompanyFlows.map((item) => (
                                    <AdminFlowsItemV2
                                        key={item.id}
                                        item={item}
                                        folderPath={getFlowFolderPath(folders, item.id)}
                                        deleteFlow={() => deleteFlow(item)}
                                        changeReadonlyFlow={changeReadonlyFlow}
                                        needUpdate={setNeedUpdateList}
                                        needUpdateCompanyFlows={getMyCompanyFlowsList}
                                        userRole={userRole}
                                        onOpenCompanyAccessModal={handleOpenCompanyAccessModal}
                                        selected={isSelected(item.id)}
                                        onToggleSelect={toggleFlow}
                                    />
                                ))}
                                {companyFlowsTotal > 0 && (
                                    <PaginationBar
                                        count={companyFlowsTotal}
                                        page={companyFlowsPage}
                                        onPageChange={(e, newPage) => updateParams({ companyPage: newPage })}
                                        rowsPerPage={companyFlowsRowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            updateParams({ companyRows: parseInt(e.target.value, 10), companyPage: 0 });
                                        }}
                                    />
                                )}
                            </>
                        )}

                        {userRole !== 'Admin' && selectedFolder === 'all' && (
                            <Typography
                                variant="h2"
                                color="primary.blackText"
                                sx={{
                                    marginBottom: '20px',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '40px',
                                }}
                            >
                                Available Pre-Defined Flows:
                            </Typography>
                        )}

                        {userRole === 'Admin' && (
                            <FlowSelectAll
                                items={flowsList}
                                selectedFlows={selectedFlows}
                                toggleVisible={toggleVisible}
                            />
                        )}
                        {userRole === 'Admin' && flowsList.map((item) => (
                            <AdminFlowsItem
                                key={`${item.name}-${item.id}`}
                                item={item}
                                folderPath={getFlowFolderPath(folders, item.id)}
                                deleteFlow={() => deleteFlow(item)}
                                changeReadonlyFlow={changeReadonlyFlow}
                                needUpdate={setNeedUpdateList}
                                userRole={userRole}
                                onOpenCompanyAccessModal={handleOpenCompanyAccessModal}
                                selected={isSelected(item.id)}
                                onToggleSelect={toggleFlow}
                            />
                        ))}
                        {(userRole === 'User' || userRole === 'Company Admin') && selectedFolder === 'all' && (
                            <FlowSelectAll
                                items={flowsList}
                                selectedFlows={selectedFlows}
                                toggleVisible={toggleVisible}
                            />
                        )}
                        {(userRole === 'User' || userRole === 'Company Admin') && selectedFolder === 'all' && flowsList.map((item, idx) => {
                            return (
                                <UserPreDefinedFlowsItem
                                    key={`${item.name}-${item.id}`}
                                    item={item}
                                    linkFlowToUser={() => linkFlowToUser(item)}
                                    needUpdate={setNeedUpdateList}
                                    userRole={userRole}
                                    selected={isSelected(item.id)}
                                    onToggleSelect={toggleFlow}
                                />
                            );
                        })}

                        {flowsTotal > 0 && (userRole === 'Admin' || selectedFolder === 'all') && (
                            <PaginationBar
                                count={flowsTotal}
                                page={flowsPage}
                                onPageChange={(e, newPage) => updateParams({ flowsPage: newPage })}
                                rowsPerPage={flowsRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    updateParams({ flowsRows: parseInt(e.target.value, 10), flowsPage: 0 });
                                }}
                            />
                        )}

                        {selectedFolder === 'all' && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '26px',
                            }}
                        >
                            <Typography
                                variant="h2"
                                color="primary.blackText"
                                sx={{
                                    marginBottom: '20px',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                }}
                            >
                                User's Flows:
                            </Typography>

                            <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                <InputLabel id="select-user-label">Select User</InputLabel>
                                <Select
                                    labelId="select-user-label"
                                    value={user}
                                    label="Select User"
                                    onChange={e => {
                                        setUserAndGetFlows(e.target.value);
                                    }}
                                >
                                    {usersList.map(userItem => (
                                        <MenuItem key={userItem.id} value={userItem.id}>{userItem.email}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FlowSelectAll
                                items={userFlowList}
                                selectedFlows={selectedFlows}
                                toggleVisible={toggleVisible}
                            />
                            {userFlowList.map((item) => (
                                <AdminFlowsItemV2
                                    key={item.id}
                                    item={item}
                                    deleteFlow={() => deleteFlowUser(item)}
                                    changeReadonlyFlow={changeReadonlyFlow}
                                    needUpdate={setNeedUpdateList}
                                    needUpdateCompanyFlows={getMyCompanyFlowsList}
                                    userRole={userRole}
                                    onOpenCompanyAccessModal={handleOpenCompanyAccessModal}
                                    selected={isSelected(item.id)}
                                    onToggleSelect={toggleFlow}
                                />
                            ))}

                            {userFlowList.length === 0 && user && (
                                <Typography
                                    sx={{
                                        marginBottom: '20px',
                                        fontWeight: 500,
                                        fontSize: '20px',
                                        lineHeight: '24px',
                                        color: '#D76A6A',
                                    }}
                                >
                                    User flows list is empty
                                </Typography>
                            )}

                            {userFlowsTotal > 0 && (
                                <PaginationBar
                                    count={userFlowsTotal}
                                    page={userFlowsPage}
                                    onPageChange={(e, newPage) => updateParams({ userPage: newPage })}
                                    rowsPerPage={userFlowsRowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        updateParams({ userRows: parseInt(e.target.value, 10), userPage: 0 });
                                    }}
                                />
                            )}
                        </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <CreateFolderModal
                open={modal.open}
                onClose={closeFolderModal}
                folders={folders}
                defaultParentId={modal.parentId}
                editFolder={modal.editFolder}
                onSaved={fetchFolders}
            />
            <CompanyAccessModal
                open={companyAccessModalOpen}
                flowId={selectedFlowForAccess}
                onClose={handleCloseCompanyAccessModal}
                onSave={handleSaveCompanyAccess}
            />
        </>
    );
};

export default AdminFlowsPage;
